import React, { useState } from "react";
import { Pencil, Check, X } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export function RenameIdea({ projectId, name, onRenamed, className = "", compact = false }) {
  const { t } = useLang();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(name);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const v = value.trim();
    if (v.length < 2 || v === name) { setEditing(false); setValue(name); return; }
    setSaving(true);
    try {
      const { data } = await api.patch(`/projects/${projectId}/name`, { name: v });
      toast.success(t("detail.renamed"));
      setEditing(false);
      onRenamed?.(data.name);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    setSaving(false);
  };

  if (!editing) {
    return (
      <div className={`flex items-start gap-2 ${compact ? "justify-start min-w-0" : "justify-center md:justify-start"} ${className}`}>
        <h1 className={`cpenz-display text-[#0b0f1a] break-words ${compact ? "text-xl sm:text-2xl leading-tight" : "text-3xl sm:text-4xl"}`} data-testid="detail-name">{name}</h1>
        <button type="button" data-testid="rename-btn" aria-label={t("detail.rename")} title={t("detail.rename")} onClick={() => { setValue(name); setEditing(true); }}
          className={`${compact ? "-mt-0.5 h-7 w-7" : "mt-1.5 sm:mt-2.5 h-9 w-9"} shrink-0 inline-flex items-center justify-center rounded-full text-slate-400 hover:text-[#0b0f1a] hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7c3aed]`}>
          <Pencil size={15} />
        </button>
      </div>
    );
  }
  return (
    <form onSubmit={(e) => { e.preventDefault(); save(); }} className={`flex flex-wrap items-center gap-2 ${className}`} data-testid="rename-form">
      <input autoFocus data-testid="rename-input" value={value} onChange={(e) => setValue(e.target.value)} maxLength={80} placeholder={t("detail.rename_ph")} aria-label={t("detail.rename_ph")}
        onKeyDown={(e) => { if (e.key === "Escape") { setEditing(false); setValue(name); } }}
        className="flex-1 min-w-[200px] rounded-2xl border border-slate-300 bg-white px-4 py-2 text-2xl sm:text-3xl font-bold tracking-tight text-[#0b0f1a] outline-none focus:border-[#7c3aed]" />
      <button type="submit" data-testid="rename-save" disabled={saving || value.trim().length < 2} className="h-11 px-4 rounded-full bg-[#0b0f1a] text-white text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"><Check size={15} /> {t("detail.rename_save")}</button>
      <button type="button" data-testid="rename-cancel" onClick={() => { setEditing(false); setValue(name); }} className="h-11 px-4 rounded-full border border-slate-200 text-sm font-semibold text-slate-600 inline-flex items-center gap-1.5"><X size={15} /> {t("detail.rename_cancel")}</button>
    </form>
  );
}
